import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { apiAxios } from "../../utils/apiAxios";
import { getAuthToken } from "../../utils/auth";
import { ROUTES } from "../../Routes/routes";
import "../../style/slotbooking.css";
import "../../style/slotmodule.css";
import {
  SLOT_TAG,
  SlotStatus,
  formatSlotDay,
  formatSlotTime,
  getSlotEndTime,
  getSlotStatus,
  normalizeYMD,
  parseYMD,
  toYMD,
} from "../../utils/slotBooking";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

type Tab = "upcoming" | "ready" | "progress" | "completed";

const TAB_OF: Record<SlotStatus, Tab> = {
  Scheduled: "upcoming",
  Rescheduled: "upcoming",
  "Ready for Scan": "ready",
  Missed: "completed",
  "Scan Uploaded": "progress",
  Processing: "progress",
  "Out for Delivery": "progress",
  Delivered: "completed",
  Cancelled: "completed",
};

const STATUS_CLASS: Record<SlotStatus, string> = {
  Scheduled: "b-blue",
  Rescheduled: "b-blue",
  "Ready for Scan": "b-amber",
  Missed: "b-red",
  "Scan Uploaded": "b-violet",
  Processing: "b-violet",
  "Out for Delivery": "b-violet",
  Delivered: "b-green",
  Cancelled: "b-red",
};

const ScheduledOrders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("upcoming");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState<Date | null>(null);
  const [, tick] = useState(0);

  const fetchSlots = useCallback(async () => {
    try {
      setLoading(true);
      const clinicId = Number(sessionStorage.getItem("selectedClinicId"));
      if (!clinicId) {
        setOrders([]);
        return;
      }
      const res = await apiAxios.post(
        `${API_BASE}/Dentist/GetAllCasesDashBoard`,
        { clinicId },
        {
          headers: {
            AuthorizationData: getAuthToken(),
            "Content-Type": "application/json",
          },
        }
      );
      const data: any[] = res.data?.singleData || [];
      const slots = data
        .filter((x) => x.slotDate || x.isSlotBooking || x.dentistTag === SLOT_TAG)
        .sort((a, b) =>
          `${normalizeYMD(a.slotDate)}${a.slotTime || ""}`.localeCompare(
            `${normalizeYMD(b.slotDate)}${b.slotTime || ""}`
          )
        );
      setOrders(slots);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load scheduled orders.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  // status time ke saath badalta hai -> har minute refresh
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const withStatus = useMemo(
    () => orders.map((o) => ({ o, status: getSlotStatus(o) })),
    [orders, tick]
  );

  const counts = useMemo(() => {
    const c: Record<Tab, number> = { upcoming: 0, ready: 0, progress: 0, completed: 0 };
    withStatus.forEach(({ status }) => (c[TAB_OF[status]] += 1));
    return c;
  }, [withStatus]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const day = dateFilter ? toYMD(dateFilter) : "";
    return withStatus.filter(({ o, status }) => {
      if (TAB_OF[status] !== tab) return false;
      if (day && normalizeYMD(o.slotDate) !== day) return false;
      if (q) {
        const hay = `${o.orderID} ${o.patientName}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [withStatus, tab, search, dateFilter]);

  const scheduleNew = () =>
    navigate(ROUTES.DENTIST.DENTIST_DASHBOARD, { state: { slotAction: "new" } });

  const openOrder = (o: any) =>
    navigate(ROUTES.DENTIST.DENTIST_DASHBOARD, { state: { slotOrderId: o.caseID } });

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: "upcoming", label: "Upcoming", icon: "bi-calendar-event" },
    { key: "ready", label: "Ready for Scan", icon: "bi-cloud-arrow-up" },
    { key: "progress", label: "In Progress", icon: "bi-gear" },
    { key: "completed", label: "Completed", icon: "bi-check2-circle" },
  ];

  return (
    <div className="slotmod">
      {/* ---------- HEADER ---------- */}
      <div className="slotmod-hero">
        <div className="slotmod-hero-text">
          <span className="slotmod-eyebrow">
            <i className="bi bi-lightning-charge-fill"></i> 90-minute express
          </span>
          <h2>Scheduled Orders</h2>
          <p>Book a slot, upload the scan when it starts, get delivery in 90 minutes.</p>
        </div>
        <button className="slotmod-cta" onClick={scheduleNew}>
          <i className="bi bi-plus-lg"></i> Schedule Order
        </button>
      </div>

      {/* ---------- TABS + FILTERS ---------- */}
      <div className="slotmod-panel">
        <div className="slotmod-tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`slotmod-tab ${tab === t.key ? "active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              <i className={`bi ${t.icon}`}></i> {t.label}
              <span className="slotmod-count">{counts[t.key]}</span>
            </button>
          ))}
        </div>

        <div className="slotmod-search-row">
          <div className="slotmod-search">
            <i className="bi bi-search"></i>
            <input
              type="text"
              placeholder="Search order / patient"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="slotmod-datefilter">
            <DatePicker
              selected={dateFilter}
              onChange={(d: Date | null) => setDateFilter(d)}
              placeholderText="Date"
              dateFormat="dd MMM yyyy"
              isClearable
              className="form-control modern-input"
            />
          </div>
        </div>

        {/* ---------- LIST ---------- */}
        {loading ? (
          <div className="slotmod-skeletons">
            {[1, 2, 3].map((i) => (
              <div className="slotmod-skel" key={i}></div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="slotmod-empty">
            <i className="bi bi-calendar2-plus"></i>
            <h6>No scheduled orders here</h6>
            <p>Schedule an order and it will show up in this list.</p>
            <button className="slotmod-cta small" onClick={scheduleNew}>
              <i className="bi bi-plus-lg"></i> Schedule Order
            </button>
          </div>
        ) : (
          <div className="slotmod-timeline">
            {visible.map(({ o, status }) => (
              <div
                className={`slotmod-card st-${TAB_OF[status]}`}
                key={o.caseID}
                onClick={() => openOrder(o)}
              >
                <div className="slotmod-body">
                  <div className="slotmod-title">
                    <strong>{o.orderID}</strong>
                    <span className="slotmod-order">{o.patientName || "Patient"}</span>
                  </div>
                  <div className="slotmod-meta">
                    <span>
                      <i className="bi bi-calendar-event"></i> {formatSlotDay(o.slotDate)},{" "}
                      {formatSlotTime(o.slotTime)}
                    </span>
                    <span>
                      <i className="bi bi-truck"></i> Expected by{" "}
                      {formatSlotTime(getSlotEndTime(o.slotTime)) || "-"}
                    </span>
                  </div>
                </div>

                <div className="slotmod-side">
                  <span className={`slotmod-badge ${STATUS_CLASS[status]}`}>{status}</span>
                  <button
                    className={status === "Ready for Scan" ? "slotmod-upload" : "slotmod-view"}
                    onClick={(e) => {
                      e.stopPropagation();
                      openOrder(o);
                    }}
                  >
                    {status === "Ready for Scan" ? (
                      <>
                        <i className="bi bi-cloud-arrow-up"></i> Upload Scan
                      </>
                    ) : (
                      "View Details"
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduledOrders;
